<template>
  <div class="roomItem__Outer" :id="data?.external_id ? data.external_id : null">
    <div class="roomItem">
      <div class="roomItem__ImagesWrapper">
        <div v-if="data.available_units_count && parseInt(data.available_units_count) < 2" v-text="$t('onlyOneLeft')" class="roomItem__RoomsCounter" />
        <div v-if="data.details?.images?.length">
          <button @click="openGallery" class="roomItem__MobileGalleryButton">
            <div v-if="data.details.images.length > 1">
              <img src="/resources/images/icons/gallery.svg" width="24" height="24" />
            </div>
            <Flare v-if="data.details.images[0]" :srcset="{ meta: data.details.images[0] }" size="both" width="644" height="470" crop="fill" />
          </button>
          <Modal mobile-only v-model="dialogs.gallery">
            <FlickitySlider class="roomItem__Slider" :options="flickityOptions" :key="data.details?.images?.length || 'room1'">
              <div v-for="image in data.details.images" :key="image.id" class="listingItem__ImageWrapper">
                <Flare v-if="image" :srcset="{ meta: image }" size="both" width="800" height="600" crop="fill" />
              </div>
            </FlickitySlider>
          </Modal>
        </div>
      </div>
  
      <div class="roomItem__Data">
        <div>
          <h2>{{ data.details.name }}</h2>
          <Occupancy :data="data.details" />
          <div v-if="data.details.description" v-text="data.details.description" class="roomItem__Description" />
          <button @click="open('details')" class="button__Text--Underline roomItem__DetailsButton">{{ $t('roomDetails') }}</button>
        </div>
  
        <div>
          <div v-if="data.price?.amount" class="roomItem__Price">
            <p>{{ $t('from') }} {{ formatPrice(data.price.amount, data.price.currency, lang) }} <template v-if="data.price?.price_type && data.price.price_type !== 'PackageMin'">{{ $t(`prices.${formatStayTextId(data.price.price_type)}`).toLowerCase() }}</template></p>
          </div>
          <button @click="toggleRates" class="button--Primary medium roomItem__RatesButton" :class="{'isActive': ratesVisible, 'disabled': !data.price?.amount || isFetchingData || disableRatesButton}">
            <template v-if="ratesVisible">{{ $t('hideRates') }}</template>
            <template v-else>{{ $t('showRates') }}</template>
            <Icon name="dropdown" />
          </button>
        </div>
      </div>
    </div>

    <div class="roomItem__RatesWrapper" :class="{'isActive': ratesVisible}">
      <div>
        <div class="roomItem__Rates">
          <Tabs v-if="data.packages?.length && groupedRates && Object.keys(groupedRates).length" :tabs="tabs" v-model="activeTab" rounded>
            <template #rates>
              <PricelistSection
                @open-modal="emit('open-modal', $event)"
                v-for="item in groupedRates"
                :data="item"
                :key="item.id"
                :external-id="data.external_id"
                :unit="data.external_id"
                :accommodationMealPlans="data.accommodationMealPlans || null"
              />
            </template>
            <template #packages>
              <PricelistSection
                @open-modal="emit('open-modal', $event)"
                v-for="item in data.packages"
                package
                :data="item"
                :key="item.id"
                :external-id="data.external_id"
                :unit="data.external_id"
              />
            </template>
          </Tabs>
          <div v-else-if="groupedRates && Object.keys(groupedRates).length">
            <PricelistSection
              @open-modal="emit('open-modal', $event)"
              v-for="item in groupedRates"
              :data="item"
              :key="item.id"
              :external-id="data.external_id"
              :unit="data.external_id"
              :accommodationMealPlans="data.accommodationMealPlans || null"
            />
          </div>
          <div v-else-if="data.packages?.length">
            <PricelistSection
              @open-modal="emit('open-modal', $event)"
              v-for="item in data.packages"
              package
              :data="item"
              :key="item.id"
              :external-id="data.external_id"
              :unit="data.external_id"
              :accommodationMealPlans="data.accommodationMealPlans || null"
            />
          </div>
        </div>
      </div>
    </div>

    <Modal v-model="dialogs.details" class="roomItem__Modal">
      <div v-if="data.details?.images?.length" class="roomItem__ModalImage">
        <Flare v-if="data.details.images[0]" :srcset="{ meta: data.details.images[0] }" size="both" width="644" height="470" crop="fill" />
      </div>
      <div class="roomItem__ModalContent">
        <h3 v-text="data.details.name" />
        <Occupancy :data="data.details" />
  
        <div class="roomItem__ModalDescription">
          {{ data.details.description }}
        </div>
  
        <div v-if="data.details?.amenities?.length" class="roomItem__ModalAmenities">
          <h4 v-text="$t('modalAmenities')" />
          <ul>
            <li v-for="amenity in data.details.amenities">
              {{ amenity.name }}
            </li>
          </ul>
        </div>
      </div>
    </Modal>
  </div>
</template>

<script setup>
  const { t } = useI18n();
  const { lang } = useLanguage();
  const emit = defineEmits(['open-modal'])
  const props = defineProps({
    data: {},
    fetchingData: {
      type: Boolean,
      default: false
    },
    disableRatesButton: {
      type: Boolean,
      default: false
    }
  })
  const ratesVisible = ref(false);
  const tabs = [
    {
      title: t('rates'),
      value: 'rates'
    },
    {
      title: t('packages'),
      value: 'packages'
    },
  ]
  const activeTab = ref(tabs[0].value);

  const dialogs = reactive({
    gallery: false,
    details: false,
  });
  const { open } = useModal(dialogs);

  const pageDotsVisible = computed(() => props.data?.details?.images?.length > 1);
  const flickityOptions = {
    watchCSS: true,
    pageDots: pageDotsVisible.value,
  };

  const isFetchingData = computed(() => props.fetchingData);

  const groupedRates = computed(() => {
    if (!props.data?.rates && !props.data?.rates?.length) return [];
    let _data = Array.from(props.data.rates);
    // Standard rate is default pricelist and it has a price that we use as a cross price for other pricelists. They have is_standard property
    // pricelists are grouped by grouping_key that our backend sends
    const standardRates = _data.filter(item => item.is_standard);

    const groupedPrices = _data.reduce((acc, item) => {
      acc[item.grouping_key] ? acc[item.grouping_key].push(item) : acc[item.grouping_key] = [item];
      return acc;
    }, {});

    if (standardRates?.length) {
      for (let group in groupedPrices) {
        for (let item of groupedPrices[group]) {
          if (!item.is_standard) {
            item.standardRatePrice = standardRates.find(rate => rate.kind === item.kind).price;
          }
        }
      }
    }

    return groupedPrices;
  })

  function toggleRates() {
    ratesVisible.value = !ratesVisible.value;
  }

  function openGallery() {
    if (props.data.details?.images?.length > 1) open('gallery');
  }

  watch(() => isFetchingData.value, value => {
    if (value) ratesVisible.value = false
  })
</script>

<style lang="scss">
  @import url('@/assets/styles/components/_roomItem.scss');
</style>